import { format } from "date-fns";

import { EmailStatus } from "constants/emails";
import { SUNO_COMMS_MESSAGE_TYPE_EMAIL } from "constants/messages";

import type {
  ContactInboxInfo,
  ContactMessage,
  ContactRawMessage
} from "../types/lead-contact-message.type";
import type { LeadContactConversation } from "../types/lead-contact.conversation.type";

const EMAIL_STATUS_MAP = new Map<string, number>([
  ["PENDING", EmailStatus.Pending],
  ["SENT", EmailStatus.Sent],
  ["DELIVERY_DELAYED", EmailStatus["Delivery Delayed"]],
  ["DELIVERED", EmailStatus.Delivered],
  ["OPENED", EmailStatus.Opened],
  ["CLICKED", EmailStatus.Clicked],
  ["COMPLAINED", EmailStatus.Complained],
  ["FAILED", EmailStatus.Failed],
  ["MUTED", EmailStatus.Muted],
  ["BOUNCED", EmailStatus.Bounced],
  ["RECEIVED", EmailStatus.Received]
]);

export const getEmailStatus = (status: string | number) => {
  return typeof status === "number"
    ? status
    : (EMAIL_STATUS_MAP.get(status.toUpperCase()) ?? EmailStatus.Pending);
};

export const formatContactMessage = (
  message: ContactRawMessage
): ContactMessage => {
  const {
    id,
    direction,
    status,
    type,
    subject,
    text,
    is_read,
    created_at,
    user,
    contact
  } = message;

  const is_email = type === SUNO_COMMS_MESSAGE_TYPE_EMAIL;

  return {
    id,
    is_email,
    direction,
    status: is_email ? getEmailStatus(status) : Number(status),
    type,
    subject,
    text,
    // Email messages should always be marked as read
    is_read: is_email ? true : Boolean(is_read),
    created_at,
    user: user
      ? {
          ...user,
          fullName: [user.first_name, user.last_name].filter(Boolean).join(" ")
        }
      : null,
    contact: {
      id: contact.id,
      fullName: [contact.first_name, contact.last_name]
        .filter(Boolean)
        .join(" ")
    }
  };
};

export const formatContactInbox = (
  contact: ContactInboxInfo
): LeadContactConversation => {
  const lastMessage = contact.most_recent_message;

  let dateMask = "MM/dd h:mm a";
  let inboundDateMask = "MM/dd h:mm a";

  if (
    lastMessage?.created_at &&
    new Date(lastMessage.created_at).getFullYear() !== new Date().getFullYear()
  ) {
    dateMask = "MM/dd/yyyy h:mm a";
  }

  if (
    lastMessage?.created_at &&
    new Date(lastMessage.created_at).getFullYear() !== new Date().getFullYear()
  ) {
    inboundDateMask = "MM/dd/yyyy h:mm a";
  }

  return {
    contactId: String(contact.id),
    fullName: [contact.first_name, contact.last_name].filter(Boolean).join(" "),
    lastMessageDate: lastMessage
      ? format(new Date(lastMessage.created_at), dateMask)
      : "",
    lastTextingMessageDate: lastMessage
      ? format(new Date(lastMessage.created_at), inboundDateMask)
      : "",
    lastTextingMessageTime: lastMessage
      ? new Date(lastMessage?.created_at).getTime()
      : 0,
    lastMessageTime: lastMessage
      ? new Date(lastMessage?.created_at).getTime()
      : 0,
    lastTextingMessageText: lastMessage?.text,
    unreadMessagesCount: Number(contact.unread_inbound_message_count || 0),
    lastMessageText: lastMessage?.text,
    lastMessageId: lastMessage?.id,
    isActive: true,
    canonicalContactId: contact.canonical_contact_id
  };
};
